import { Skeleton } from "antd";
import { useMemo } from "react";
import { GenericTableProps } from "../../components/tables/GenericTable";

export default function useTableDataAndColumns<Model>(
  results: GenericTableProps<Model>["results"],
  columns: GenericTableProps<Model>["columns"],
  hasFetched: boolean,
  isLoading: boolean,
) {
  /**
   * Trick on "_dataSource" & "_columns"
   * 1. Prevent "No Data" message from Ant Design when data is loading
   * 2. Display skeletons consistently for a better loading experience
   * 3. Maintain table structure and avoid type errors during initial load
   */
  const finalDataSource = useMemo(() => {
    if (isLoading || !hasFetched) {
      return Array.from({ length: 10 }, (_, index) => ({
        id: index,
      })) as Model[];
    }
    return results;
  }, [results, hasFetched, isLoading]);

  const finalColumns = useMemo(() => {
    return columns.map((column) => ({
      ...column,
      render: (text: string, record: Model, i: number) => {
        const showSkeleton = isLoading || !hasFetched;

        if (showSkeleton) {
          return (
            <Skeleton
              key={column.key as React.Key}
              title
              active
              paragraph={false}
            />
          );
        }

        if (column.render) {
          return column.render(text, record, i);
        }

        return text;
      },
    }));
  }, [columns, hasFetched, isLoading]);

  return { finalDataSource, finalColumns };
}
